/** True for any of the shapes the API has used for a boolean-ish "enabled" flag. */
export const isAutoUpgradeEnabled = (value) => ['1', 1, true, 'true'].includes(value);

// An ends-by date is stored as UTC midnight of the selected calendar day. The latest a
// real inhabited timezone's local end-of-day for that date can occur is ~35h after that
// instant (worst realistic offset ~UTC-11, e.g. American Samoa), so 36h is the tightest
// round bound that still never under-warns for any real-world timezone.
export const SCHEDULE_ENDS_BY_GRACE_MS = 36 * 60 * 60 * 1000;

/** Normalizes an epoch value (accepts seconds or milliseconds) to milliseconds, or null if invalid. */
export const toEpochMs = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return null;
  }
  return numeric >= 1e12 ? numeric : numeric * 1000;
};

/**
 * Given a GET_FIRMWARE_POLICY response payload, returns whether a scheduled auto-upgrade
 * is still pending (could still fire) - not just whether it was ever enabled. The stored
 * autoUpgrade flag stays enabled even after a schedule can no longer fire, so warn about a
 * manual update only while a scheduled run is still possible.
 */
export const isScheduledUpgradePending = (policy = {}) => {
  if (!isAutoUpgradeEnabled(policy.autoUpgrade)) {
    return false;
  }
  if (String(policy.frequency ?? '0') === '0') {
    // One-time (Repeats = None): only pending while the window hasn't ended yet.
    const endMs = toEpochMs(policy.endTime);
    return endMs === null || endMs > Date.now();
  }
  if (String(policy.recurrence?.type ?? '') === '2') {
    // Recurring with an "ends by date": pending unless that date has passed.
    const endsByMs = toEpochMs(policy.recurrence.value);
    return endsByMs === null || endsByMs + SCHEDULE_ENDS_BY_GRACE_MS > Date.now();
  }
  // No end condition, or "ends after N occurrences" (not reliably computable from
  // fire history alone) - conservatively treat as still pending.
  return true;
};
